import { type MutableRefObject, useEffect, useRef } from 'react';

import {
  useIsReleaseChronicleBulkSuccessOnChunkUploadDesktopEnabled,
  useIsReleaseDesktopBleFixedReconnectCadenceMacEnabled,
  useIsReleaseDesktopBleFixedReconnectCadenceWindowsEnabled,
  useIsReleaseRemoteSyncAsyncWebEnabled,
  useIsRemoteRecordingDedupDesktopEnabled,
} from '@/features/flags';
import { setBulkSuccessOnChunkUploadEnabled } from '@/tauri/chronicle/bulk-success-on-chunk-upload';
import { setFixedReconnectCadenceMacEnabled } from '@/tauri/chronicle/fixed-reconnect-cadence-mac';
import { setFixedReconnectCadenceWindowsEnabled } from '@/tauri/chronicle/fixed-reconnect-cadence-windows';
import {
  setRemoteRecordingDedupEnabled,
  setRemoteSyncAsyncEnabled,
} from '@/tauri/chronicle/remote-sync-async';

/**
 * Push Chronicle Flagsmith flags into Rust (APP-7397 remote-sync-async,
 * HAR-1808 bulk SUCCESS-on-chunk-upload, the macOS and Windows reconnect
 * kill switches). Rust defaults the first two to false and the kill switches
 * to true until FE pushes. Returns a ref mirror of remote-sync-async for the
 * state-event handler without re-subscribing.
 */
export function useRemoteSyncAsyncFlagPush(): MutableRefObject<boolean> {
  const isRemoteSyncAsyncEnabled = useIsReleaseRemoteSyncAsyncWebEnabled();
  const isRemoteRecordingDedupEnabled = useIsRemoteRecordingDedupDesktopEnabled();
  const isRemoteSyncAsyncEnabledRef = useRef(isRemoteSyncAsyncEnabled);
  const isBulkSuccessOnChunkUploadEnabled =
    useIsReleaseChronicleBulkSuccessOnChunkUploadDesktopEnabled();
  const isFixedReconnectCadenceMacEnabled =
    useIsReleaseDesktopBleFixedReconnectCadenceMacEnabled();
  const isFixedReconnectCadenceWindowsEnabled =
    useIsReleaseDesktopBleFixedReconnectCadenceWindowsEnabled();
  useEffect(() => {
    isRemoteSyncAsyncEnabledRef.current = isRemoteSyncAsyncEnabled;
    void setRemoteSyncAsyncEnabled(isRemoteSyncAsyncEnabled);
  }, [isRemoteSyncAsyncEnabled]);
  useEffect(() => {
    void setBulkSuccessOnChunkUploadEnabled(isBulkSuccessOnChunkUploadEnabled);
  }, [isBulkSuccessOnChunkUploadEnabled]);
  useEffect(() => {
    void setFixedReconnectCadenceMacEnabled(isFixedReconnectCadenceMacEnabled);
  }, [isFixedReconnectCadenceMacEnabled]);
  useEffect(() => {
    void setFixedReconnectCadenceWindowsEnabled(isFixedReconnectCadenceWindowsEnabled);
  }, [isFixedReconnectCadenceWindowsEnabled]);
  useEffect(() => {
    void setRemoteRecordingDedupEnabled(isRemoteRecordingDedupEnabled);
  }, [isRemoteRecordingDedupEnabled]);
  return isRemoteSyncAsyncEnabledRef;
}
